import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCommunity, commApi, photoUrl, API } from './CommunityContext';
import { Avatar, AuthDialog, InterestPills, AvatarStack } from './CommunityShared';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Anchor,
  Ship,
  MapPin,
  Calendar,
  Users,
  Send,
  MessageCircle,
  ArrowLeft,
  Plus,
  Trash2,
  CalendarPlus,
  Compass,
  UserPlus,
  Check,
  Loader2,
  Clock,
  Image as ImageIcon,
  Share2,
  Timer,
  SmilePlus,
  Search,
} from 'lucide-react';

const CHAT_REACTIONS = ['👍', '❤️', '😂', '🎉', '😮'];

const ReactionPicker = ({ open, onToggle, onPick }) => (
  <div className="relative">
    <button
      onClick={onToggle}
      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-cyan-600 p-1"
      data-testid="reaction-add-btn"
      title="Aggiungi reazione"
    >
      <SmilePlus className="w-4 h-4" />
    </button>
    {open && (
      <div className="absolute z-20 bottom-full mb-1 left-1/2 -translate-x-1/2 flex gap-1 bg-white border border-slate-200 rounded-full shadow-lg px-2 py-1">
        {CHAT_REACTIONS.map((e) => (
          <button
            key={e}
            onClick={() => onPick(e)}
            className="text-lg hover:scale-125 transition-transform"
            data-testid={`reaction-option-${e}`}
          >
            {e}
          </button>
        ))}
      </div>
    )}
  </div>
);

const ImageLightbox = ({ src, title, onClose }) => (
  <Dialog open={!!src} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl p-2 bg-black/95 border-0" data-testid="image-lightbox">
      {src && <img src={src} alt={title} className="w-full h-auto rounded-lg max-h-[82vh] object-contain" />}
      {title && <p className="text-center text-white/80 text-sm py-2">{title}</p>}
    </DialogContent>
  </Dialog>
);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
const fmtTime = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  return dt.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const daysUntil = (d) => {
  if (!d) return null;
  const dep = new Date(d);
  dep.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((dep - today) / 86400000);
};

const shareOnWhatsApp = (cruise) => {
  const url = `${window.location.origin}/community/cruise/${cruise.id}`;
  const msg =
    `🚢 Sto organizzando la crociera su *${cruise.ship_name}* da ${cruise.departure_city} ` +
    `(${fmtDate(cruise.departure_date)})!\n\nUnisciti alla nostra community per conoscerci prima di partire, ` +
    `organizzare cene ed escursioni insieme 🌊\n\n${url}`;
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
};

const CountdownBadge = ({ date }) => {
  const days = daysUntil(date);
  if (days === null) return null;
  let label;
  if (days > 1) label = `${days} giorni alla partenza`;
  else if (days === 1) label = 'Domani si parte!';
  else if (days === 0) label = 'Si parte oggi! 🎉';
  else label = 'Viaggio in corso o concluso';
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-100"
      data-testid="countdown-badge"
    >
      <Timer className="w-4 h-4 text-cyan-300" />
      <span className="text-sm font-semibold whitespace-nowrap">{label}</span>
    </div>
  );
};

const CommunityCruise = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { member, loading } = useCommunity();
  const [cruise, setCruise] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [notMember, setNotMember] = useState(false);
  const [tab, setTab] = useState('members');

  const loadCruise = async () => {
    try {
      const { data } = await commApi.get(`/community/cruises/${groupId}`);
      setCruise(data);
    } catch (e) {
      toast.error('Crociera non trovata');
      navigate('/community');
    }
  };

  useEffect(() => {
    loadCruise();
  }, [groupId]);

  const join = async () => {
    try {
      await commApi.post('/community/cruises/join', {
        ship_name: cruise.ship_name,
        departure_date: cruise.departure_date,
        departure_city: cruise.departure_city,
      });
      setNotMember(false);
      toast.success('Sei a bordo!');
      loadCruise();
      setTab('members');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  if (loading || !cruise) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const needAuth = !member;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white relative overflow-hidden">
        {cruise.image_url && (
          <div className="absolute inset-0">
            <img src={cruise.image_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/50" />
          </div>
        )}
        <div className="max-w-5xl mx-auto px-4 py-5 relative">
          <button onClick={() => navigate('/community')} className="text-cyan-300 hover:text-white text-sm flex items-center gap-1 mb-4" data-testid="back-to-community">
            <ArrowLeft className="w-4 h-4" /> Community
          </button>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
                <Ship className="w-7 h-7 text-cyan-400" /> {cruise.ship_name}
              </h1>
              <div className="flex items-center gap-4 mt-2 text-slate-300 text-sm flex-wrap">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-cyan-400" /> {cruise.departure_city}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-cyan-400" /> {fmtDate(cruise.departure_date)}</span>
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-cyan-400" /> {cruise.member_count} a bordo</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CountdownBadge date={cruise.departure_date} />
              <Button
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => shareOnWhatsApp(cruise)}
                data-testid="whatsapp-invite-btn"
              >
                <Share2 className="w-4 h-4 mr-2" /> Invita amici
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {needAuth ? (
          <Card className="text-center py-12">
            <CardContent>
              <Anchor className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-800">Accedi per vedere la tua tribù</h2>
              <p className="text-slate-500 mt-2 mb-5">Iscriviti gratis per vedere i profili, chattare e organizzare eventi.</p>
              <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setAuthOpen(true)} data-testid="cruise-auth-btn">
                Accedi / Iscriviti
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-5 w-full max-w-3xl mb-6">
              <TabsTrigger value="members" data-testid="tab-members"><Users className="w-4 h-4 mr-1.5" /> Membri</TabsTrigger>
              <TabsTrigger value="chat" data-testid="tab-chat"><MessageCircle className="w-4 h-4 mr-1.5" /> Chat</TabsTrigger>
              <TabsTrigger value="events" data-testid="tab-events"><CalendarPlus className="w-4 h-4 mr-1.5" /> Eventi</TabsTrigger>
              <TabsTrigger value="excursions" data-testid="tab-excursions"><Compass className="w-4 h-4 mr-1.5" /> Escursioni</TabsTrigger>
              <TabsTrigger value="gallery" data-testid="tab-gallery"><ImageIcon className="w-4 h-4 mr-1.5" /> Galleria</TabsTrigger>
            </TabsList>

            <TabsContent value="members">
              <MembersTab groupId={groupId} onNotMember={() => setNotMember(true)} />
            </TabsContent>
            <TabsContent value="chat">
              <ChatTab groupId={groupId} onNotMember={() => setNotMember(true)} />
            </TabsContent>
            <TabsContent value="events">
              <EventsTab groupId={groupId} />
            </TabsContent>
            <TabsContent value="excursions">
              <ExcursionsTab groupId={groupId} />
            </TabsContent>
            <TabsContent value="gallery">
              <GalleryTab groupId={groupId} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AuthDialog open={authOpen} onClose={() => { setAuthOpen(false); loadCruise(); }} defaultMode="register" />

      <Dialog open={notMember} onOpenChange={setNotMember}>
        <DialogContent data-testid="join-prompt-dialog">
          <DialogHeader>
            <DialogTitle>Unisciti a questa crociera</DialogTitle>
            <DialogDescription>Per interagire devi prima unirti al gruppo di {cruise.ship_name}.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotMember(false)}>Annulla</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={join} data-testid="confirm-join-btn">Unisciti</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ---------- MEMBERS ---------- */
const MembersTab = ({ groupId, onNotMember }) => {
  const navigate = useNavigate();
  const { member: me } = useCommunity();
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    commApi
      .get(`/community/cruises/${groupId}/members`)
      .then((r) => setMembers(r.data))
      .catch((e) => {
        if (e.response?.status === 403) onNotMember();
      })
      .finally(() => setLoading(false));
  }, [groupId]);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-cyan-500 mx-auto my-10" />;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? members.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          (m.interests || []).some((i) => i.toLowerCase().includes(q)) ||
          (m.bio || '').toLowerCase().includes(q)
      )
    : members;

  return (
    <>
      <div className="relative w-full sm:max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="members-search-input"
          className="pl-9"
          placeholder="Cerca per nome o interesse..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-10 text-slate-400">Nessun membro trovato per "{query}"</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((m) => (
            <Card key={m.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelected(m)} data-testid={`member-card-${m.id}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <Avatar member={m} size={52} />
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate flex items-center gap-1.5">
                    {m.name} {m.id === me?.id && <span className="text-xs text-cyan-600">(tu)</span>}
                  </p>
                  {m.bio && <p className="text-xs text-slate-500 truncate">{m.bio}</p>}
                  <div className="mt-1"><InterestPills interests={(m.interests || []).slice(0, 2)} /></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent data-testid="member-profile-dialog">
          {selected && (
            <>
              <div className="flex flex-col items-center text-center">
                {selected.photo_path ? (
                  <button
                    onClick={() => setLightbox(selected)}
                    className="rounded-full focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-transform hover:scale-105"
                    data-testid="member-photo-zoom"
                    title="Tocca per ingrandire"
                  >
                    <Avatar member={selected} size={90} />
                  </button>
                ) : (
                  <Avatar member={selected} size={90} />
                )}
                <h3 className="text-xl font-bold text-slate-800 mt-3">{selected.name}</h3>
                {selected.bio && <p className="text-slate-500 mt-1">{selected.bio}</p>}
                <div className="mt-3"><InterestPills interests={selected.interests} /></div>
                {selected.id !== me?.id && (
                  <Button
                    className="mt-5 bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => navigate(`/community/messages/${selected.id}`)}
                    data-testid="message-member-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" /> Scrivi in privato
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox foto profilo */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-lg p-2 bg-black/95 border-0" data-testid="photo-lightbox">
          {lightbox?.photo_path && (
            <img
              src={photoUrl(lightbox.photo_path)}
              alt={lightbox.name}
              className="w-full h-auto rounded-lg max-h-[80vh] object-contain"
            />
          )}
          <p className="text-center text-white/80 text-sm py-2">{lightbox?.name}</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ---------- CHAT ---------- */
const ChatTab = ({ groupId, onNotMember }) => {
  const { member: me } = useCommunity();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);
  const endRef = useRef();

  const react = async (messageId, emoji) => {
    setPickerFor(null);
    try {
      await commApi.post(`/community/messages/${messageId}/react`, { emoji });
      load();
    } catch (e) {
      toast.error('Reazione non riuscita');
    }
  };

  const load = async () => {
    try {
      const { data } = await commApi.get(`/community/cruises/${groupId}/messages`);
      setMessages(data);
    } catch (e) {
      if (e.response?.status === 403) onNotMember();
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [groupId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await commApi.post(`/community/cruises/${groupId}/messages`, { text });
      setText('');
      load();
    } catch (e) {
      toast.error('Invio fallito');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="h-[460px] overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
          {messages.length === 0 && (
            <p className="text-center text-slate-400 py-10">Nessun messaggio. Rompi il ghiaccio! 👋</p>
          )}
          {messages.map((m) => {
            const mine = m.author?.id === me?.id;
            const reactions = m.reactions || {};
            const hasReactions = Object.keys(reactions).length > 0;
            return (
              <div key={m.id} className={`flex gap-2 group ${mine ? 'flex-row-reverse' : ''}`}>
                <Avatar member={m.author} size={34} />
                <div className={`max-w-[75%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                  <div className="flex items-center gap-1">
                    {mine && (
                      <ReactionPicker open={pickerFor === m.id} onToggle={() => setPickerFor(pickerFor === m.id ? null : m.id)} onPick={(e) => react(m.id, e)} />
                    )}
                    <div className={`rounded-2xl px-3.5 py-2 ${mine ? 'bg-cyan-600 text-white rounded-tr-sm' : 'bg-white border border-slate-200 rounded-tl-sm'}`}>
                      {!mine && <p className="text-xs font-semibold text-cyan-700 mb-0.5">{m.author?.name}</p>}
                      <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                    </div>
                    {!mine && (
                      <ReactionPicker open={pickerFor === m.id} onToggle={() => setPickerFor(pickerFor === m.id ? null : m.id)} onPick={(e) => react(m.id, e)} />
                    )}
                  </div>
                  {hasReactions && (
                    <div className={`flex flex-wrap gap-1 mt-1 ${mine ? 'justify-end' : ''}`}>
                      {Object.entries(reactions).map(([emoji, users]) => {
                        const reacted = users.includes(me?.id);
                        return (
                          <button
                            key={emoji}
                            onClick={() => react(m.id, emoji)}
                            data-testid={`reaction-chip-${m.id}-${emoji}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors ${
                              reacted ? 'bg-cyan-100 border-cyan-300 text-cyan-800' : 'bg-white border-slate-200 text-slate-600 hover:border-cyan-300'
                            }`}
                          >
                            <span>{emoji}</span>
                            <span className="font-semibold">{users.length}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <span className="text-[10px] text-slate-400 mt-0.5 px-1">{fmtTime(m.created_at)}</span>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t flex gap-2">
          <Input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
            placeholder="Scrivi un messaggio..."
          />
          <Button data-testid="chat-send-btn" onClick={send} disabled={sending} className="bg-cyan-600 hover:bg-cyan-700 px-4">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------- image upload helper ---------- */
const useImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await commApi.post('/community/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data.image_path;
    } catch (e) {
      toast.error('Upload immagine fallito');
      return null;
    } finally {
      setUploading(false);
    }
  };
  return { upload, uploading };
};

/* ---------- EVENTS ---------- */
const EventsTab = ({ groupId }) => {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', event_date: '', location: '', image_path: null });
  const [saving, setSaving] = useState(false);
  const [imgView, setImgView] = useState(null);
  const { upload, uploading } = useImageUpload();
  const fileRef = useRef();

  const load = () => commApi.get(`/community/cruises/${groupId}/events`).then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, [groupId]);

  const create = async () => {
    if (!form.title.trim()) return toast.error('Inserisci un titolo');
    setSaving(true);
    try {
      await commApi.post(`/community/cruises/${groupId}/events`, form);
      setForm({ title: '', description: '', event_date: '', location: '', image_path: null });
      setOpen(false);
      load();
      toast.success('Evento creato!');
    } catch (e) {
      toast.error('Errore');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id) => {
    await commApi.post(`/community/events/${id}/toggle-attend`);
    load();
  };
  const remove = async (id) => {
    await commApi.delete(`/community/events/${id}`);
    load();
    toast.success('Evento eliminato');
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) {
      const path = await upload(f);
      if (path) setForm((s) => ({ ...s, image_path: path }));
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <p className="text-slate-500 text-sm">Organizza cene, aperitivi, feste e ritrovi a bordo.</p>
        <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setOpen(true)} data-testid="new-event-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Nuovo evento
        </Button>
      </div>
      {events.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-dashed">
          <CalendarPlus className="w-10 h-10 mx-auto mb-2 opacity-40" /> Nessun evento. Crea il primo!
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {events.map((e) => (
            <Card key={e.id} data-testid={`event-card-${e.id}`} className="overflow-hidden">
              {e.image_path && (
                <img
                  src={photoUrl(e.image_path)}
                  alt={e.title}
                  className="w-full h-36 object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                  onClick={() => setImgView({ src: photoUrl(e.image_path), title: e.title })}
                  data-testid={`event-image-${e.id}`}
                />
              )}
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <h3 className="font-bold text-slate-800">{e.title}</h3>
                  {e.is_mine && (
                    <button onClick={() => remove(e.id)} className="text-slate-400 hover:text-rose-500" data-testid={`delete-event-${e.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {e.description && <p className="text-sm text-slate-500 mt-1">{e.description}</p>}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                  {e.event_date && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {e.event_date}</span>}
                  {e.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <AvatarStack members={e.attendees_members || []} size={26} />
                    <span className="text-sm text-slate-600">{e.attendees_count} partecipanti</span>
                  </div>
                  <Button
                    size="sm"
                    variant={e.is_attending ? 'default' : 'outline'}
                    className={e.is_attending ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
                    onClick={() => toggle(e.id)}
                    data-testid={`attend-event-${e.id}`}
                  >
                    {e.is_attending ? <><Check className="w-4 h-4 mr-1" /> Partecipi</> : <><UserPlus className="w-4 h-4 mr-1" /> Partecipa</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader><DialogTitle>Nuovo evento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo</Label><Input data-testid="event-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Aperitivo al tramonto" /></div>
            <div><Label>Descrizione</Label><Textarea data-testid="event-desc-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quando</Label><Input data-testid="event-date-input" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} placeholder="Es. 1ª sera, ore 19" /></div>
              <div><Label>Dove</Label><Input data-testid="event-loc-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Es. Bar piscina" /></div>
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="event-image-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-1.5" />}
                {form.image_path ? 'Immagine caricata ✓' : 'Aggiungi immagine'}
              </Button>
            </div>
            <Button className="w-full bg-cyan-600 hover:bg-cyan-700" onClick={create} disabled={saving} data-testid="save-event-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crea evento
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImageLightbox src={imgView?.src} title={imgView?.title} onClose={() => setImgView(null)} />
    </>
  );
};

/* ---------- EXCURSIONS ---------- */
const ExcursionsTab = ({ groupId }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', port: '', date: '', max_people: 0, image_path: null });
  const [saving, setSaving] = useState(false);
  const [imgView, setImgView] = useState(null);
  const { upload, uploading } = useImageUpload();
  const fileRef = useRef();

  const load = () => commApi.get(`/community/cruises/${groupId}/excursions`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [groupId]);

  const create = async () => {
    if (!form.title.trim()) return toast.error('Inserisci un titolo');
    setSaving(true);
    try {
      await commApi.post(`/community/cruises/${groupId}/excursions`, { ...form, max_people: parseInt(form.max_people) || 0 });
      setForm({ title: '', description: '', port: '', date: '', max_people: 0, image_path: null });
      setOpen(false);
      load();
      toast.success('Escursione creata!');
    } catch (e) {
      toast.error('Errore');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id) => {
    try {
      await commApi.post(`/community/excursions/${id}/toggle-join`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };
  const remove = async (id) => {
    await commApi.delete(`/community/excursions/${id}`);
    load();
    toast.success('Escursione eliminata');
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) {
      const path = await upload(f);
      if (path) setForm((s) => ({ ...s, image_path: path }));
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <p className="text-slate-500 text-sm">Organizza escursioni in autonomia nei porti, senza l'agenzia.</p>
        <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setOpen(true)} data-testid="new-excursion-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Nuova escursione
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-dashed">
          <Compass className="w-10 h-10 mx-auto mb-2 opacity-40" /> Nessuna escursione. Proponi la prima!
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((e) => (
            <Card key={e.id} data-testid={`excursion-card-${e.id}`} className="overflow-hidden">
              {e.image_path && (
                <img
                  src={photoUrl(e.image_path)}
                  alt={e.title}
                  className="w-full h-36 object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                  onClick={() => setImgView({ src: photoUrl(e.image_path), title: e.title })}
                  data-testid={`excursion-image-${e.id}`}
                />
              )}
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <h3 className="font-bold text-slate-800">{e.title}</h3>
                  {e.is_mine && (
                    <button onClick={() => remove(e.id)} className="text-slate-400 hover:text-rose-500" data-testid={`delete-excursion-${e.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {e.description && <p className="text-sm text-slate-500 mt-1">{e.description}</p>}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                  {e.port && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.port}</span>}
                  {e.date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {e.date}</span>}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <AvatarStack members={e.participants_members || []} size={26} />
                    <span className="text-sm text-slate-600">{e.participants_count}{e.max_people > 0 ? ` / ${e.max_people}` : ''}</span>
                  </div>
                  <Button
                    size="sm"
                    variant={e.is_joined ? 'default' : 'outline'}
                    className={e.is_joined ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
                    onClick={() => toggle(e.id)}
                    disabled={!e.is_joined && e.is_full}
                    data-testid={`join-excursion-${e.id}`}
                  >
                    {e.is_joined ? <><Check className="w-4 h-4 mr-1" /> Iscritto</> : e.is_full ? 'Al completo' : <><UserPlus className="w-4 h-4 mr-1" /> Unisciti</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="excursion-dialog">
          <DialogHeader><DialogTitle>Nuova escursione</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo</Label><Input data-testid="excursion-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Tour dei calanchi" /></div>
            <div><Label>Descrizione</Label><Textarea data-testid="excursion-desc-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Porto</Label><Input data-testid="excursion-port-input" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} placeholder="Es. Palermo" /></div>
              <div><Label>Data</Label><Input data-testid="excursion-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="Es. 3° giorno" /></div>
              <div><Label>Max persone</Label><Input data-testid="excursion-max-input" type="number" value={form.max_people} onChange={(e) => setForm({ ...form, max_people: e.target.value })} placeholder="0 = illimitato" /></div>
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="excursion-image-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-1.5" />}
                {form.image_path ? 'Immagine caricata ✓' : 'Aggiungi immagine'}
              </Button>
            </div>
            <Button className="w-full bg-cyan-600 hover:bg-cyan-700" onClick={create} disabled={saving} data-testid="save-excursion-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crea escursione
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImageLightbox src={imgView?.src} title={imgView?.title} onClose={() => setImgView(null)} />
    </>
  );
};

/* ---------- GALLERY ---------- */
const GalleryTab = ({ groupId }) => {
  const { member: me } = useCommunity();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [imgView, setImgView] = useState(null);
  const fileRef = useRef();
  const token = localStorage.getItem('community_token');

  const fileUrl = (id) => `${API}/community/gallery/file/${id}?auth=${token}`;

  const load = () =>
    commApi.get(`/community/cruises/${groupId}/gallery`).then((r) => setPhotos(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, [groupId]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name}: troppo grande (max 10MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        await commApi.post(`/community/cruises/${groupId}/gallery`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await load();
      toast.success('Foto caricate!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fallito');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const remove = async (id) => {
    try {
      await commApi.delete(`/community/gallery/${id}`);
      setPhotos((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-cyan-500 mx-auto my-10" />;

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <p className="text-slate-500 text-sm">Condividi le foto del viaggio con la tua tribù.</p>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
        <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="gallery-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          Carica foto
        </Button>
      </div>
      {photos.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-dashed">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" /> Nessuna foto ancora. Carica la prima!
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative group rounded-lg overflow-hidden bg-slate-100 aspect-square" data-testid={`gallery-photo-${p.id}`}>
              <img
                src={fileUrl(p.id)}
                alt={p.member_name}
                loading="lazy"
                className="w-full h-full object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                onClick={() => setImgView({ src: fileUrl(p.id), title: `Caricata da ${p.member_name}` })}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 pointer-events-none">
                <span className="text-white text-xs font-medium">{p.member_name}</span>
              </div>
              {p.is_mine && (
                <button
                  onClick={() => remove(p.id)}
                  className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-rose-500 hover:text-white text-slate-600 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`gallery-delete-${p.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <ImageLightbox src={imgView?.src} title={imgView?.title} onClose={() => setImgView(null)} />
    </>
  );
};

export default CommunityCruise;
